import { Box, Button, Chip, Grid, Link, Stack, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { WaterTestKitRequestsSelectors } from '../../../../store/waterTestKitRequests'
import { EquipmentChoice } from './PlanSelectionSection'
import { SmartHomeContextCard } from './SmartHomeContextCard'

// SIPP-833 / SIPP-845: feature grid shown to the right of the product image
// inside the upgraded Smart Home card. Copy lifted directly from the
// product page so the marketing pitch and the in-app pitch stay aligned.
const SMART_HOME_FEATURES: { label: string; description: string }[] = [
  {
    label: 'Proactive Leak Protection',
    description: 'Detects leaks early, helping protect your home from costly plumbing damage.',
  },
  {
    label: 'Real-Time Condition Awareness',
    description: 'Monitors your water system continuously, alerting you when conditions shift.',
  },
  {
    label: 'Elevated Drinking Water Safety',
    description: 'Integrated filtration + UV protection for broader contaminant coverage at the point of use.',
  },
  {
    label: 'Smarter System Performance',
    description: 'Uses real usage data to replace protection cartridges at the right moment, not just on schedule.',
  },
]

const SMART_HOME_PRODUCT_URL = 'https://www.sippsafely.com/sipp-smart-home'

interface Step2EquipmentPickerProps {
  equipmentChoice: EquipmentChoice
  onSelect: (choice: NonNullable<EquipmentChoice>) => void
}

interface EquipmentTile {
  key: Exclude<NonNullable<EquipmentChoice>, 'smart_home'>
  icon: string
  label: string
  subtitle: string
}

const TILES: readonly EquipmentTile[] = [
  { key: 'refrigerator', icon: '❄️', label: 'Refrigerator Filter', subtitle: 'Most common' },
  { key: 'under_sink', icon: '🚰', label: 'Under-Sink System', subtitle: 'Most effective' },
  { key: 'pitcher', icon: '🫗', label: 'Water Pitcher', subtitle: 'Most affordable' },
  { key: 'countertop', icon: '⬛', label: 'Countertop Filter', subtitle: 'No installation' },
  { key: 'whole_home', icon: '🏠', label: 'Whole-Home System', subtitle: 'Total coverage' },
]

const SYSTEMS_TO_KEY: Record<string, EquipmentTile['key']> = {
  refrigerator_filter: 'refrigerator',
  under_sink_system: 'under_sink',
  water_pitcher: 'pitcher',
  counter_top_filter: 'countertop',
  whole_home_system: 'whole_home',
}

export const Step2EquipmentPicker = ({
  equipmentChoice,
  onSelect,
}: Step2EquipmentPickerProps) => {
  const latestRequest = useSelector(WaterTestKitRequestsSelectors.makeLatestResult)

  const systems = latestRequest?.systems
  // SIPP-828: a user who answered "None / I don't use a filter" in the test
  // strip flow is pre-filtered as unfiltered. We don't preselect a tile for
  // them — they're here to choose their *first* filter, not confirm an
  // existing one. Instead we change the subtext copy below to reframe the step.
  const userHasNoFilter = Array.isArray(systems) && systems.length === 1 && systems[0] === 'none'

  const preSelectedKey: EquipmentTile['key'] | null = (() => {
    if (userHasNoFilter) return null
    if (!Array.isArray(systems)) return null
    for (const s of systems) {
      const mapped = SYSTEMS_TO_KEY[s]
      if (mapped) return mapped
    }
    return null
  })()

  // Visual-only highlight on mount: nudges the user toward the equipment
  // they previously told us about, without firing onSelect (which would
  // auto-advance the wizard and skip Step 2 entirely). Step 3 only
  // appears when the user actively clicks a tile.
  const [localHighlight, setLocalHighlight] = useState<EquipmentTile['key'] | null>(null)
  useEffect(() => {
    if (!localHighlight && preSelectedKey) {
      setLocalHighlight(preSelectedKey)
    }
  }, [localHighlight, preSelectedKey])

  const handleTileClick = (key: EquipmentTile['key']) => {
    setLocalHighlight(key)
    onSelect(key)
  }

  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" color="text.secondary">
        {userHasNoFilter
          ? "You're currently unfiltered. Choose the equipment that fits your home and we'll match your filter and set up a replacement schedule."
          : "We'll use this to set up your filter matching and replacement schedule."}
      </Typography>
      <Grid container spacing={1.5}>
        {TILES.map((tile) => {
          // Tile reads as highlighted whenever the user has either committed
          // (equipmentChoice) or it's the local visual nudge from systems[].
          const selected =
            equipmentChoice === tile.key || localHighlight === tile.key
          const showPreselectCaption = tile.key === preSelectedKey
          return (
            <Grid key={tile.key} size={{ xs: 6, md: 'auto' }} sx={{ flexGrow: { md: 1 } }}>
              <Box
                onClick={() => handleTileClick(tile.key)}
                sx={{
                  p: 2,
                  borderRadius: 1.5,
                  border: '2px solid',
                  borderColor: selected ? 'primary.dark' : 'divider',
                  bgcolor: selected ? 'background.neutral' : 'background.paper',
                  textAlign: 'center',
                  cursor: 'pointer',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.75,
                  transition: 'all 0.15s',
                  '&:hover': {
                    borderColor: 'primary.dark',
                  },
                }}
              >
                <Typography sx={{ fontSize: 32, lineHeight: 1 }}>{tile.icon}</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {tile.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {tile.subtitle}
                </Typography>
                {showPreselectCaption && (
                  <Typography
                    variant="caption"
                    color="primary.dark"
                    sx={{ fontWeight: 600 }}
                  >
                    This is what you told us
                  </Typography>
                )}
              </Box>
            </Grid>
          )
        })}
      </Grid>

      {/* SIPP-833: Smart Home tile elevated from a simple banner CTA into a
          rich product card — image + features + product link — so users have
          enough context to decide without leaving the page.
          SIPP-845: real product image in place of the placeholder + soft
          primary tint background (blue.50 / #E6EEF4) for the unselected
          state. Selected state still uses background.neutral so it pops. */}
      <Box
        sx={{
          position: 'relative',
          border: equipmentChoice === 'smart_home' ? '2px solid' : '1px solid',
          borderColor: equipmentChoice === 'smart_home' ? 'primary.dark' : 'divider',
          borderRadius: 2,
          p: { xs: 2, md: 3 },
          mt: 1.5,
          bgcolor:
            equipmentChoice === 'smart_home' ? 'background.neutral' : 'blue.50',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 3,
          alignItems: { xs: 'flex-start', md: 'center' },
        }}
      >
        <Chip
          label="UPGRADE"
          size="small"
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            bgcolor: 'primary.darker',
            color: 'common.white',
            fontWeight: 700,
            letterSpacing: 1,
          }}
        />

        {/* Left column — product image + headline + product link */}
        <Stack spacing={1.25} sx={{ flexShrink: 0, maxWidth: { xs: '100%', md: 260 } }}>
          <Box
            component="img"
            src="/images/smart-home-device.png"
            alt="SIPP Smart Home Water Monitor"
            sx={{
              width: { xs: 80, md: 120 },
              height: { xs: 80, md: 120 },
              objectFit: 'contain',
              flexShrink: 0,
            }}
          />
          <Typography variant="body1" fontWeight={700}>
            Smart Home Water Monitor
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Continuous real-time protection — always on.
          </Typography>
          <Link
            href={SMART_HOME_PRODUCT_URL}
            target="_blank"
            rel="noopener"
            variant="body2"
            sx={{ color: 'primary.main', fontWeight: 600, textDecoration: 'none' }}
          >
            Learn more →
          </Link>
        </Stack>

        {/* Right column — feature grid + CTA */}
        <Stack spacing={1.5} sx={{ flex: 1, width: '100%' }}>
          <Stack spacing={1}>
            {SMART_HOME_FEATURES.map((feature) => (
              <Box key={feature.label}>
                <Typography variant="body2" fontWeight={700} component="span">
                  {feature.label}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  component="span"
                  sx={{ ml: 0.75 }}
                >
                  — {feature.description}
                </Typography>
              </Box>
            ))}
          </Stack>
          <Button
            variant="contained"
            color="primary"
            onClick={() => onSelect('smart_home')}
            sx={{ alignSelf: { xs: 'stretch', md: 'flex-start' } }}
          >
            UPGRADE →
          </Button>
        </Stack>
      </Box>

      {/* SIPP-842: contextual card explains *why* this specific user should
          consider Smart Home. Returns null when none of the four trigger
          conditions apply, so it's a no-op for users without those signals. */}
      <SmartHomeContextCard />
    </Stack>
  )
}
